import { Link, useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { Toast, type ToastKind } from "@/components/Toast";
import {
  Archive,
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  ExternalLink,
  FileText,
  Hand,
  Image as ImageIcon,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Play,
  RefreshCw,
  Tag,
  Trophy,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { StatusBadge } from "@/components/StatusBadge";
import {
  PipelineStepper,
  STEPS,
  computeStepStates,
} from "@/components/PipelineStepper";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ReferenceImagesPanel } from "@/components/ReferenceImagesPanel";
import { PlaytomicParticipantsPanel } from "@/components/PlaytomicParticipantsPanel";
import { EmailPreviewDialog } from "@/components/EmailPreviewDialog";
import {
  AudienceSelector,
  defaultAudience,
  type AudienceSelection,
} from "@/components/AudienceSelector";
import { useDeleteTournament, useTournament } from "@/hooks/useTournaments";
import {
  useApproveTexts,
  useApproveFlyer,
  useLandingUrl,
  useRunStep,
} from "@/hooks/usePipeline";
import {
  daysBetween,
  formatItalianDate,
  formatNumber,
} from "@/lib/format";

const STATE_META: Record<string, { text: string; color: string }> = {
  ready: {
    text: "Prossimo step disponibile",
    color: "text-court-700",
  },
  await: { text: "In attesa di approvazione manuale", color: "text-amber-700" },
  running: { text: "In esecuzione…", color: "text-moving-700" },
  done: { text: "Completato", color: "text-moving-700" },
  failed: { text: "Errore — controlla log", color: "text-red-700" },
  pending: { text: "Da eseguire", color: "text-slate-500" },
};

export default function TournamentDetail() {
  const { slug } = useParams<{ slug: string }>();
  const nav = useNavigate();
  const { data, isLoading, error } = useTournament(slug);

  const [confirmDestructive, setConfirmDestructive] = useState<{
    step: 6 | 8;
    open: boolean;
  }>({ step: 6, open: false });
  const [audienceSel, setAudienceSel] =
    useState<AudienceSelection>(defaultAudience);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [toast, setToast] = useState<
    { kind: ToastKind; message: string } | null
  >(null);

  const runStep = useRunStep(slug ?? "");
  const approveTexts = useApproveTexts(slug ?? "");
  const approveFlyer = useApproveFlyer(slug ?? "");
  const landing = useLandingUrl(slug ?? undefined);
  const deleteMut = useDeleteTournament();

  if (!slug) {
    return <div className="text-sm text-slate-500">Slug mancante.</div>;
  }
  if (isLoading) {
    return <div className="text-sm text-slate-500">Caricamento…</div>;
  }
  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        Errore: {String(error)}
      </div>
    );
  }
  if (!data) {
    return <div className="text-sm text-slate-500">Non trovato.</div>;
  }

  const input = data.input.tournament ?? {};
  const state = (data.state ?? {}) as Record<string, unknown>;
  const status = (state.status ?? "DRAFT") as string;
  const stepStates = computeStepStates(status);

  const tournamentDate = input.date as string | undefined;
  const daysToTournament = tournamentDate
    ? daysBetween(new Date(), tournamentDate)
    : null;

  const categories = (input.categories ?? []) as Array<{
    name: string;
    level: string;
    max_teams: number;
    price_community: number;
    price_non_community: number;
    playtomic_link: string;
  }>;

  // Prossimo step "actionable" (ready o await)
  const sequence = [1, 2, 3, 4, 7, 5, 6, 8, 9];
  const nextStepId = sequence.find(
    (id) => stepStates[id] === "ready" || stepStates[id] === "await",
  );
  const nextStepDef = STEPS.find((s) => s.id === nextStepId);

  // Metriche
  const emailsSent = (state.emails_sent as number) ?? 0;
  const emailsFailed = (state.emails_failed as number) ?? 0;
  const waSent = (state.whatsapp_sent as number) ?? 0;
  const waFailed = (state.whatsapp_failed as number) ?? 0;
  const socialPosts = (state.social_posts_published as number) ?? 0;

  return (
    <div className="flex flex-col gap-6">
      {/* Back link */}
      <div>
        <Link
          to="/tournaments"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Tutti i tornei
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold text-slate-900">
              {input.name || slug}
            </h1>
            <StatusBadge status={status} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {tournamentDate
                ? formatItalianDate(tournamentDate, true)
                : "data non impostata"}
            </span>
            {daysToTournament !== null ? (
              <span className="text-court-600 font-medium">
                {daysToTournament > 0
                  ? `fra ${daysToTournament} giorni`
                  : daysToTournament === 0
                    ? "oggi"
                    : `${-daysToTournament} giorni fa`}
              </span>
            ) : null}
            {input.time_start ? (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {String(input.time_start)}
                {input.time_end ? `–${input.time_end}` : ""}
              </span>
            ) : null}
            {input.location ? (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {String(input.location)}
              </span>
            ) : null}
            <span className="font-mono text-xs text-slate-400">· {slug}</span>
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEmailPreviewOpen(true)}
          >
            <Mail className="h-4 w-4" />
            Anteprima email
          </Button>
          <Link to={`/tournaments/${slug}/edit`}>
            <Button variant="outline" size="sm">
              <Pencil className="h-4 w-4" />
              Modifica dati
            </Button>
          </Link>
        </div>
      </div>

      {/* Pipeline stepper */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Pipeline</CardTitle>
            {nextStepDef ? (
              <div className="text-xs text-slate-500">
                Prossimo:{" "}
                <span className="font-medium text-slate-900">
                  {nextStepDef.label}
                </span>
              </div>
            ) : status === "COMPLETED" ? (
              <div className="text-xs font-medium text-moving-700">
                Tutti gli step completati
              </div>
            ) : null}
          </div>
        </CardHeader>
        <CardBody>
          <PipelineStepper slug={slug} status={status} />
        </CardBody>
      </Card>

      {/* Two-col: Overview + Action panel */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Main overview */}
        <div className="flex flex-col gap-5">
          {/* Metriche */}
          <div className="grid grid-cols-3 gap-3">
            <MetricCard
              icon={<FileText className="h-4 w-4" />}
              label="Email inviate"
              value={formatNumber(emailsSent)}
              hint={emailsFailed > 0 ? `${emailsFailed} errori` : undefined}
            />
            <MetricCard
              icon={<FileText className="h-4 w-4" />}
              label="WA inviati"
              value={formatNumber(waSent)}
              hint={waFailed > 0 ? `${waFailed} errori` : undefined}
            />
            <MetricCard
              icon={<FileText className="h-4 w-4" />}
              label="Post social"
              value={formatNumber(socialPosts)}
              hint="pubblicati"
            />
          </div>

          {/* Dettagli torneo */}
          <Card>
            <CardHeader>
              <CardTitle>Dettagli torneo</CardTitle>
            </CardHeader>
            <CardBody>
              <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                <DataField
                  label="Formula"
                  icon={<Trophy className="h-4 w-4" />}
                  value={(input.formula as string) ?? "—"}
                />
                <DataField
                  label="Partite garantite"
                  icon={<Trophy className="h-4 w-4" />}
                  value={
                    input.guaranteed_matches
                      ? String(input.guaranteed_matches)
                      : "—"
                  }
                />
                <DataField
                  label="Sponsor"
                  icon={<Tag className="h-4 w-4" />}
                  value={(input.sponsor as string) ?? "—"}
                />
                <DataField
                  label="Durata partita"
                  icon={<Clock className="h-4 w-4" />}
                  value={
                    input.match_duration_min
                      ? `${input.match_duration_min} min`
                      : "—"
                  }
                />
              </dl>
              {(input.description_short as string) ? (
                <p className="mt-4 rounded-md bg-slate-50 p-3 text-sm italic text-slate-600">
                  "{input.description_short as string}"
                </p>
              ) : null}
              {(input.after_party as string) ? (
                <div className="mt-4 rounded-md border border-moving-200 bg-moving-50 p-3 text-sm text-moving-900">
                  <span className="font-medium">After-party: </span>
                  {input.after_party as string}
                </div>
              ) : null}
            </CardBody>
          </Card>

          {/* Categorie */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Categorie ({categories.length})</CardTitle>
              </div>
            </CardHeader>
            <CardBody className="p-0">
              {categories.length === 0 ? (
                <div className="p-5 text-sm text-slate-500">
                  Nessuna categoria definita.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {categories.map((cat, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-3 px-5 py-4"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-court-50 text-court-700">
                        <Users className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900">
                          {cat.name}
                        </div>
                        <div className="text-xs text-slate-500">
                          livello {cat.level} · max {cat.max_teams} squadre
                        </div>
                      </div>
                      <div className="text-right text-xs text-slate-700">
                        <div>
                          <span className="font-medium">
                            {cat.price_community}€
                          </span>{" "}
                          community
                        </div>
                        <div className="text-slate-500">
                          {cat.price_non_community}€ standard
                        </div>
                      </div>
                      {cat.playtomic_link ? (
                        <a
                          href={cat.playtomic_link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-court-500 hover:text-court-700"
                          aria-label="Apri su Playtomic"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          {/* Risorse */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <ResourceLink
              to={`/tournaments/${slug}/checkpoint/texts`}
              icon={<FileText className="h-5 w-5" />}
              title="Testi"
              state={stepStates[1]}
            />
            <ResourceLink
              to={`/tournaments/${slug}/checkpoint/flyer`}
              icon={<ImageIcon className="h-5 w-5" />}
              title="Locandina"
              state={stepStates[3]}
            />
            <ResourceLink
              to={`/tournaments/${slug}/sendings`}
              icon={<FileText className="h-5 w-5" />}
              title="Invii"
              state={stepStates[6]}
            />
          </div>

          {/* Landing page (se deployata) */}
          {landing.data?.deployed && landing.data.url ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4" />
                    Landing page pubblica
                  </CardTitle>
                  <a
                    href={landing.data.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium text-court-500 hover:text-court-700"
                  >
                    Apri in nuova scheda
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </CardHeader>
              <CardBody className="space-y-3">
                <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  <span className="flex-1 truncate font-mono text-xs text-slate-600">
                    {landing.data.url}
                  </span>
                  <button
                    onClick={() =>
                      navigator.clipboard.writeText(landing.data!.url!)
                    }
                    className="rounded px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200"
                  >
                    Copia
                  </button>
                </div>
                <div className="overflow-hidden rounded-md border border-slate-200">
                  <iframe
                    src={landing.data.url}
                    title="Anteprima landing"
                    loading="lazy"
                    className="h-[420px] w-full bg-white"
                  />
                </div>
              </CardBody>
            </Card>
          ) : null}

          {/* Iscritti Playtomic (solo se il torneo è stato importato) */}
          {(() => {
            const pid =
              (input as { playtomic_activity_id?: string }).playtomic_activity_id
              || ((data.input as { playtomic?: { activity_id?: string } }).playtomic?.activity_id);
            return pid ? (
              <PlaytomicParticipantsPanel activityId={pid} />
            ) : null;
          })()}

          {/* Immagini di riferimento per NB2 */}
          <ReferenceImagesPanel
            slug={slug}
            onToast={(kind, message) => setToast({ kind, message })}
          />
        </div>

        {/* Action panel */}
        <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <Card>
            <CardHeader>
              <CardTitle>Azioni</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              {nextStepId ? (
                <NextActionButton
                  stepId={nextStepId}
                  onApproveTexts={() =>
                    approveTexts.mutate(undefined, {
                      onSuccess: () =>
                        setToast({
                          kind: "success",
                          message: "Testi approvati ✓",
                        }),
                      onError: (e) =>
                        setToast({
                          kind: "error",
                          message: `Errore: ${String(e)}`,
                        }),
                    })
                  }
                  onApproveFlyer={() =>
                    approveFlyer.mutate(undefined, {
                      onSuccess: () =>
                        setToast({
                          kind: "success",
                          message: "Locandina approvata ✓",
                        }),
                      onError: (e) =>
                        setToast({
                          kind: "error",
                          message: `Errore: ${String(e)}`,
                        }),
                    })
                  }
                  onRunStep={(step) => {
                    if (step === 6 || step === 8) {
                      // Reset audience a default quando riapri il dialog
                      setAudienceSel(defaultAudience());
                      setConfirmDestructive({ step, open: true });
                      return;
                    }
                    runStep.mutate(
                      { step },
                      {
                        onSuccess: (res) =>
                          setToast({
                            kind: res.status === "ok" ? "success" : "error",
                            message:
                              res.status === "ok"
                                ? `Step ${res.step} completato (${res.duration_seconds.toFixed(1)}s)`
                                : `Step ${res.step}: ${res.error ?? "errore"}`,
                          }),
                        onError: (e) =>
                          setToast({
                            kind: "error",
                            message: `Errore step: ${String(e)}`,
                          }),
                      },
                    );
                  }}
                  loading={
                    runStep.isPending ||
                    approveTexts.isPending ||
                    approveFlyer.isPending
                  }
                />
              ) : status === "COMPLETED" ? (
                <div className="text-sm text-moving-700">
                  🏆 Torneo completato.
                </div>
              ) : (
                <div className="text-sm text-slate-500">
                  Nessuna azione disponibile.
                </div>
              )}

              <div className="border-t border-slate-100 pt-3 text-xs text-slate-500">
                <div className="font-medium text-slate-700">Dry-run</div>
                <div>
                  Gli step di invio reale (6 email, 8 WA) richiedono conferma
                  esplicita. Dry-run è sempre disponibile via API.
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Stato info */}
          <Card>
            <CardBody className="space-y-2 text-xs text-slate-500">
              <div>
                <span className="font-medium text-slate-700">Stato: </span>
                <span className={STATE_META[stepStates[nextStepId ?? 9] ?? "done"].color}>
                  {STATE_META[stepStates[nextStepId ?? 9] ?? "done"].text}
                </span>
              </div>
              <div className="font-mono text-[10px] text-slate-400">
                slug: {slug}
              </div>
            </CardBody>
          </Card>

          {/* Azioni avanzate: permette di ri-eseguire qualsiasi step anche se
              è già stato completato. Utile per inviare email/WA a un audience
              diverso senza dover aspettare il "prossimo step". */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Azioni avanzate</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2">
              <div className="text-xs text-slate-500">
                Rie­segui manualmente uno step specifico. Step 6 e 8 apriranno
                il selettore audience prima dell'invio.
              </div>
              <div className="grid grid-cols-1 gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    runStep.mutate(
                      { step: 5 },
                      {
                        onSuccess: () =>
                          setToast({
                            kind: "success",
                            message: "Email HTML rigenerata ✓",
                          }),
                        onError: (e) =>
                          setToast({
                            kind: "error",
                            message: `Errore step 5: ${String(e)}`,
                          }),
                      },
                    )
                  }
                  loading={runStep.isPending}
                  className="w-full justify-start"
                >
                  <FileText className="h-4 w-4" />
                  5 · Rigenera email HTML
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEmailPreviewOpen(true)}
                  className="w-full justify-start"
                >
                  <Mail className="h-4 w-4" />
                  Anteprima email
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    setAudienceSel(defaultAudience());
                    setConfirmDestructive({ step: 6, open: true });
                  }}
                  className="w-full justify-start"
                >
                  <Play className="h-4 w-4" />
                  6 · Invia email (con audience)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    runStep.mutate(
                      { step: 7 },
                      {
                        onSuccess: () =>
                          setToast({
                            kind: "success",
                            message: "Landing ricostruita ✓",
                          }),
                        onError: (e) =>
                          setToast({
                            kind: "error",
                            message: `Errore step 7: ${String(e)}`,
                          }),
                      },
                    )
                  }
                  loading={runStep.isPending}
                  className="w-full justify-start"
                >
                  <ExternalLink className="h-4 w-4" />
                  7 · Rigenera landing
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    setAudienceSel(defaultAudience());
                    setConfirmDestructive({ step: 8, open: true });
                  }}
                  className="w-full justify-start"
                >
                  <Play className="h-4 w-4" />
                  8 · Invia WhatsApp (con audience)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    runStep.mutate(
                      { step: 9 },
                      {
                        onSuccess: () =>
                          setToast({
                            kind: "success",
                            message: "Social eseguiti ✓",
                          }),
                        onError: (e) =>
                          setToast({
                            kind: "error",
                            message: `Errore step 9: ${String(e)}`,
                          }),
                      },
                    )
                  }
                  loading={runStep.isPending}
                  className="w-full justify-start"
                >
                  <RefreshCw className="h-4 w-4" />
                  9 · Pubblica social oggi
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Zona pericolosa */}
          <Card className="border-red-200">
            <CardHeader className="border-red-100">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Archive className="h-4 w-4" />
                Zona pericolosa
              </CardTitle>
            </CardHeader>
            <CardBody className="space-y-2 text-xs text-slate-600">
              <p>
                Archivia il torneo (soft-delete): il YAML viene spostato in{" "}
                <code className="rounded bg-slate-100 px-1 font-mono">
                  tournaments_data/tournaments/archive/
                </code>
                . Lo stato e gli artefatti generati (testi, locandine, landing)
                restano su disco, ma il torneo non appare più nelle liste.
              </p>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setConfirmDelete(true)}
                className="w-full"
              >
                <Archive className="h-4 w-4" />
                Archivia torneo
              </Button>
            </CardBody>
          </Card>
        </aside>
      </div>

      {/* Confirm dialogs */}
      <ConfirmDialog
        open={confirmDestructive.open}
        title={
          confirmDestructive.step === 6
            ? "Invio email reali"
            : "Invio WhatsApp reali"
        }
        description={
          <div className="space-y-4">
            <AudienceSelector
              channel={confirmDestructive.step === 6 ? "email" : "whatsapp"}
              value={audienceSel}
              onChange={setAudienceSel}
            />
            <p className="text-xs font-medium text-red-700">
              ⚠ Non è reversibile una volta partito.
              Digita lo slug qui sotto per confermare.
            </p>
          </div>
        }
        confirmText={
          confirmDestructive.step === 6 ? "Invia email" : "Invia WhatsApp"
        }
        variant="danger"
        typeToConfirm={slug}
        loading={runStep.isPending}
        onConfirm={() => {
          runStep.mutate(
            {
              step: confirmDestructive.step,
              body: {
                confirm_slug: slug,
                audience: audienceSel.audience,
                audience_min_tournaments: audienceSel.min_tournaments,
                audience_since: audienceSel.since,
                audience_level_ranges: audienceSel.level_ranges,
                audience_tournament_name_contains:
                  audienceSel.tournament_name_contains,
                audience_gender: audienceSel.gender,
              },
            },
            {
              onSuccess: (res) =>
                setToast({
                  kind: res.status === "ok" ? "success" : "error",
                  message:
                    res.status === "ok"
                      ? `Invio avviato: step ${res.step}`
                      : `Errore: ${res.error ?? "sconosciuto"}`,
                }),
              onError: (e) =>
                setToast({ kind: "error", message: `Errore: ${String(e)}` }),
              onSettled: () =>
                setConfirmDestructive((s) => ({ ...s, open: false })),
            },
          );
        }}
        onClose={() =>
          setConfirmDestructive((s) => ({ ...s, open: false }))
        }
      />

      {/* Confirm archive (delete) */}
      <ConfirmDialog
        open={confirmDelete}
        title={`Archiviare "${input.name || slug}"?`}
        description={
          <div className="space-y-2">
            <p>
              Il YAML del torneo verrà spostato nella cartella{" "}
              <code className="rounded bg-slate-100 px-1 font-mono text-xs">
                archive/
              </code>
              . Questa operazione può essere annullata manualmente via SSH
              riportando il file indietro.
            </p>
            <p>
              Gli artefatti già generati (testi, locandine, landing pubblicata)
              <strong> non vengono cancellati</strong> dal filesystem.
            </p>
          </div>
        }
        confirmText="Archivia"
        variant="danger"
        typeToConfirm={slug}
        loading={deleteMut.isPending}
        onConfirm={() => {
          deleteMut.mutate(slug, {
            onSuccess: (res) => {
              setConfirmDelete(false);
              setToast({
                kind: "success",
                message: `Archiviato in ${res.archived_to.split("/").pop()}`,
              });
              setTimeout(() => nav("/tournaments"), 900);
            },
            onError: (e) => {
              setConfirmDelete(false);
              setToast({
                kind: "error",
                message: `Errore archiviazione: ${String(e)}`,
              });
            },
          });
        }}
        onClose={() => setConfirmDelete(false)}
      />

      <EmailPreviewDialog
        open={emailPreviewOpen}
        slug={slug}
        onClose={() => setEmailPreviewOpen(false)}
      />

      {toast ? (
        <Toast
          kind={toast.kind}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────────

function MetricCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          {icon}
          {label}
        </div>
        <div className="mt-1.5 text-xl font-semibold text-slate-900">
          {value}
        </div>
        {hint ? <div className="text-xs text-red-600">{hint}</div> : null}
      </CardBody>
    </Card>
  );
}

function DataField({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div>
      <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
        {icon}
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-slate-900">{value}</dd>
    </div>
  );
}

function ResourceLink({
  to,
  icon,
  title,
  state,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  state: string;
}) {
  const badge =
    state === "done"
      ? "Completato"
      : state === "await"
        ? "Da approvare"
        : state === "ready"
          ? "Pronto"
          : state === "failed"
            ? "Errore"
            : "In attesa";
  const color =
    state === "done"
      ? "bg-moving-50 text-moving-700 border-moving-200"
      : state === "await"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : state === "ready"
          ? "bg-court-50 text-court-700 border-court-200"
          : state === "failed"
            ? "bg-red-50 text-red-700 border-red-200"
            : "bg-slate-50 text-slate-500 border-slate-200";
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:bg-slate-50"
    >
      <div className="rounded-md bg-slate-100 p-2 text-slate-700">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900">{title}</div>
        <span
          className={`mt-0.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium border ${color}`}
        >
          {badge}
        </span>
      </div>
      <ArrowRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

function NextActionButton({
  stepId,
  onApproveTexts,
  onApproveFlyer,
  onRunStep,
  loading,
}: {
  stepId: number;
  onApproveTexts: () => void;
  onApproveFlyer: () => void;
  onRunStep: (step: number) => void;
  loading: boolean;
}) {
  const def = STEPS.find((s) => s.id === stepId);
  if (!def) return null;

  // Checkpoint: 2 = approva testi, 4 = approva locandina
  if (stepId === 2) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-amber-800">
          Rivedi i testi prima di approvare.
        </div>
        <div className="flex flex-col gap-2">
          <Link
            to={`/tournaments/${window.location.pathname.split("/")[2]}/checkpoint/texts`}
          >
            <Button variant="outline" size="md" className="w-full">
              <Hand className="h-4 w-4" />
              Apri checkpoint testi
            </Button>
          </Link>
          <Button
            variant="primary"
            size="md"
            onClick={onApproveTexts}
            loading={loading}
            className="w-full"
          >
            <Play className="h-4 w-4" />
            Approva testi
          </Button>
        </div>
      </div>
    );
  }
  if (stepId === 4) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-amber-800">
          Verifica le 3 locandine generate.
        </div>
        <div className="flex flex-col gap-2">
          <Link
            to={`/tournaments/${window.location.pathname.split("/")[2]}/checkpoint/flyer`}
          >
            <Button variant="outline" size="md" className="w-full">
              <Hand className="h-4 w-4" />
              Apri checkpoint locandina
            </Button>
          </Link>
          <Button
            variant="primary"
            size="md"
            onClick={onApproveFlyer}
            loading={loading}
            className="w-full"
          >
            <Play className="h-4 w-4" />
            Approva locandina
          </Button>
        </div>
      </div>
    );
  }

  const label =
    stepId === 6
      ? "Invia email (reali)"
      : stepId === 8
        ? "Invia WhatsApp (reali)"
        : stepId === 3
          ? "Genera locandina (NB2)"
          : stepId === 9
            ? "Pubblica social oggi"
            : `Esegui: ${def.label}`;

  return (
    <Button
      variant={stepId === 6 || stepId === 8 ? "danger" : "primary"}
      size="md"
      onClick={() => onRunStep(stepId)}
      loading={loading}
      className="w-full"
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : stepId === 3 ? (
        <RefreshCw className="h-4 w-4" />
      ) : (
        <Play className="h-4 w-4" />
      )}
      {label}
    </Button>
  );
}

